/**
 * 现代化 MediaPipe FaceLandmarker 适配器
 * 使用最新的 @mediapipe/tasks-vision 包
 * 提供与旧版本 FaceMesh 兼容的接口
 */

import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
export class ModernFaceMesh {
    constructor() {
        this.faceLandmarker = null;
        this.isInitialized = false;
        this.onResultsCallback = null;
        this.options = {
            maxNumFaces: 1,
            minDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5,
        };
        
        console.log('🆕 ModernFaceMesh 实例已创建');
        
        // 兼容旧版本的常量
        this.FACEMESH_LIPS = [[61,146],[146,91],[91,181],[181,84],[84,17],[17,314],[314,405],[405,321],[321,375],[375,291],[61,185],[185,40],[40,39],[39,37],[37,0],[0,267],[267,269],[269,270],[270,409],[409,291],[78,95],[95,88],[88,178],[178,87],[87,14],[14,317],[317,402],[402,318],[318,324],[324,308],[78,191],[191,80],[80,81],[81,82],[82,13],[13,312],[312,311],[311,310],[310,415],[415,308]];
        this.FACEMESH_LEFT_EYE = [[263,249],[249,390],[390,373],[373,374],[374,380],[380,381],[381,382],[382,362],[263,466],[466,388],[388,387],[387,386],[386,385],[385,384],[384,398],[398,362]];
        this.FACEMESH_RIGHT_EYE = [[33,7],[7,163],[163,144],[144,145],[145,153],[153,154],[154,155],[155,133],[33,246],[246,161],[161,160],[160,159],[159,158],[158,157],[157,173],[173,133]];
        this.FACEMESH_FACE_OVAL = [[10,338],[338,297],[297,332],[332,284],[284,251],[251,389],[389,356],[356,454],[454,323],[323,361],[361,288],[288,397],[397,365],[365,379],[379,378],[378,400],[400,377],[377,152],[152,148],[148,176],[176,149],[149,150],[150,136],[136,172],[172,58],[58,132],[132,93],[93,234],[234,127],[127,162],[162,21],[21,54],[54,103],[103,67],[67,109],[109,10]];
    }

    /**
     * 初始化 FaceLandmarker
     */
    async initialize() {
        try {
            console.log('🚀 初始化现代化 MediaPipe FaceLandmarker...');
            
            // 解析文件集
            // const vision = await FilesetResolver.forVisionTasks(
            //     "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
            // );
            // // 创建 FaceLandmarker
            // this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
            //     baseOptions: {
            //         modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            //         delegate: "GPU"
            //     },
            //     outputFaceBlendshapes: false,
            //     outputFacialTransformationMatrixes: false,
            //     runningMode: "VIDEO",
            //     numFaces: this.options.maxNumFaces,
            //     minFaceDetectionConfidence: this.options.minDetectionConfidence,
            //     minFacePresenceConfidence: this.options.minTrackingConfidence,
            //     minTrackingConfidence: this.options.minTrackingConfidence
            // });

            const vision = await FilesetResolver.forVisionTasks(
                "/mediapipe/wasm/"
            );
            this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
                baseOptions: {
                    modelAssetPath: "/mediapipe/models/face_landmarker.task",
                    delegate: "GPU"
                },
                outputFaceBlendshapes: false,
                outputFacialTransformationMatrixes: false,
                runningMode: "VIDEO",
                numFaces: this.options.maxNumFaces,
                minFaceDetectionConfidence: this.options.minDetectionConfidence,
                minFacePresenceConfidence: this.options.minTrackingConfidence,
                minTrackingConfidence: this.options.minTrackingConfidence
            });
            
            this.isInitialized = true;
            console.log('✅ 现代化 MediaPipe FaceLandmarker 初始化成功');
            
        } catch (error) {
            console.error('❌ 现代化 MediaPipe FaceLandmarker 初始化失败:', error);
            throw error;
        }
    }

    /**
     * 设置选项（兼容旧版本接口）
     */
    setOptions(options) {
        this.options = { ...this.options, ...options };
        console.log('⚙️ 现代版本选项已设置:', this.options);
        
        // 如果已经初始化，需要重新创建实例
        if (this.isInitialized && this.faceLandmarker) {
            console.log('🔄 重新应用选项设置...');
            // 注意：现代版本可能需要重新初始化才能应用新选项
        }
    }

    /**
     * 设置结果回调函数（兼容旧版本接口）
     */
    onResults(callback) {
        this.onResultsCallback = callback;
        console.log('📞 现代版本结果回调已设置');
    }

    /**
     * 处理视频帧
     */
    async send(input) {
        if (!this.isInitialized || !this.faceLandmarker) {
            console.error('❌ FaceLandmarker 未初始化');
            return;
        }

        try {
            const timestamp = performance.now();
            const results = this.faceLandmarker.detectForVideo(input.image, timestamp);
            
            // 转换为旧版本兼容的格式
            const compatibleResults = this.convertToLegacyFormat(results, input.image);
            
            // 调用回调函数
            if (this.onResultsCallback) {
                this.onResultsCallback(compatibleResults);
            }
            
        } catch (error) {
            console.error('❌ 现代版本人脸检测失败:', error);
            
            // 返回空结果，保持兼容性
            if (this.onResultsCallback) {
                this.onResultsCallback({
                    multiFaceLandmarks: [],
                    image: input.image
                });
            }
        }
    }

    /**
     * 将现代版本结果转换为旧版本兼容格式
     */
    convertToLegacyFormat(modernResults, image) {
        const multiFaceLandmarks = [];
        
        if (modernResults.faceLandmarks && modernResults.faceLandmarks.length > 0) {
            // 现代版本返回的是 478 个关键点
            // 直接使用，因为旧版本也是 468 个点（接近）
            multiFaceLandmarks.push(modernResults.faceLandmarks[0]);
        }
        
        return {
            multiFaceLandmarks: multiFaceLandmarks,
            image: image
        };
    }

    /**
     * 清理资源
     */
    close() {
        if (this.faceLandmarker) {
            this.faceLandmarker.close();
            this.faceLandmarker = null;
        }
        this.isInitialized = false;
        console.log('🧹 现代版本资源已清理');
    }
}

// 默认导出
export default ModernFaceMesh; 